import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import { useSelector } from 'react-redux';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import TopicRoundedIcon from '@mui/icons-material/TopicRounded';
import SchemaRoundedIcon from '@mui/icons-material/SchemaRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import { isAmxAdmin, isAmxUser, isPartnerAdmin } from '../utils/auth';

const homeItem = { text: 'Home', icon: <HomeRoundedIcon />, route: '/' };

const organizationItems = [
  { text: 'My Work', icon: <WorkRoundedIcon />, route: '/my-work' },
  { text: 'Business Units', icon: <BusinessRoundedIcon />, route: '/business-units', match: (path) => path.startsWith('/business-units') },
  { text: 'Capabilities', icon: <PrecisionManufacturingRoundedIcon />, route: '/my-capabilities' },
  { text: 'Assets', icon: <Inventory2RoundedIcon />, route: '/assets', match: (path) => path === '/assets' },
  { text: 'Users', icon: <ManageAccountsRoundedIcon />, route: '/users', match: (path) => path.startsWith('/users') },
];

const exchangeItems = [
  { text: 'Explore Capabilities', icon: <ExploreRoundedIcon />, route: '/capabilities-explorer' },
  { text: 'Requests', icon: <AssignmentRoundedIcon />, route: '/requests' },
  { text: 'Workspaces', icon: <PeopleRoundedIcon />, route: '/workspaces-dashboard' },
];

const secondaryListItems = [
  { text: 'About', icon: <InfoRoundedIcon />, route: '/about' },
  { text: 'Feedback', icon: <HelpRoundedIcon />, route: '/feedback' },
];

const adminListItems = [
  { text: 'Admin Dashboard', icon: <AdminPanelSettingsRoundedIcon />, route: '/admin' },
  { text: 'Partner Registry', icon: <PeopleRoundedIcon />, route: '/admin/partners' },
  { text: 'Users', icon: <ManageAccountsRoundedIcon />, route: '/admin/users' },
  { text: 'MQTT Topics', icon: <TopicRoundedIcon />, route: '/admin/mqtt-topics' },
  { text: 'Gateways', icon: <HubRoundedIcon />, route: '/admin/gateways' },
  { text: 'Need Categories', icon: <RuleRoundedIcon />, route: '/admin/need-categories', match: (path) => path.startsWith('/admin/need-categories') },
  { text: 'Asset Types', icon: <CategoryRoundedIcon />, route: '/admin/asset-types' },
  { text: 'Account Security', icon: <SecurityRoundedIcon />, route: '/admin/account-security' },
  { text: 'Workspaces & Messages', icon: <ForumRoundedIcon />, route: '/admin/workspaces' },
  { text: 'Demo Workspace', icon: <AccountTreeRoundedIcon />, route: '/admin/demo-workspace' },
];

const adminAdvancedItems = [
  { text: 'Schema Registry', icon: <SchemaRoundedIcon />, route: '/admin/shared-schemas' },
];

const adminReportItems = [
  { text: 'Activity Logs', icon: <ManageSearchRoundedIcon />, route: '/admin/activity-logs' },
  { text: 'Account Compliance', icon: <FactCheckRoundedIcon />, route: '/admin/account-compliance' },
];

export default function MenuContent() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const admin = isAmxAdmin(user);
  const adminOnly = admin && !isAmxUser(user);
  const partnerAdmin = isPartnerAdmin(partnerProfile);
  const partnerUser = isAmxUser(user);
  const path = window.location.pathname;

  const renderItems = (items) => items.map((item, index) => (
    <ListItem key={`${item.text}-${index}`} disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        onClick={() => navigate(item.route)}
        selected={item.match ? item.match(path) : path === item.route}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    </ListItem>
  ));

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <Stack spacing={2}>
        {!adminOnly ? (
          <>
            <List dense>
              {renderItems([homeItem])}
            </List>

            {partnerUser ? (
              <>
                <List dense>
                  <ListSubheader component="div" disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2 }}>
                    MY ORGANIZATION
                  </ListSubheader>
                  {renderItems(organizationItems)}
                </List>

                <List dense>
                  <ListSubheader component="div" disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2 }}>
                    EXCHANGE
                  </ListSubheader>
                  {renderItems(exchangeItems)}
                </List>

                {partnerAdmin ? (
                  <List dense>
                    <ListSubheader component="div" disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2 }}>
                      ADMINISTRATION
                    </ListSubheader>
                    {renderItems([{ text: 'Partner Admin', icon: <AdminPanelSettingsRoundedIcon />, route: '/partner-admin' }])}
                  </List>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        {admin ? (
          <List dense>
            <ListSubheader component="div" disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2 }}>
              Admin
            </ListSubheader>
            {adminListItems.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => navigate(item.route)}
                  selected={item.match ? item.match(path) : window.location.pathname === item.route}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
            <ListSubheader component="div" disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2, mt: 1 }}>
              Reports
            </ListSubheader>
            {adminReportItems.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => navigate(item.route)}
                  selected={window.location.pathname === item.route}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
            <ListSubheader component="div" disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2, mt: 1 }}>
              Advanced
            </ListSubheader>
            {adminAdvancedItems.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => navigate(item.route)}
                  selected={window.location.pathname === item.route}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : null}
      </Stack>

      <List dense>
        {secondaryListItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ display: 'block' }}>
            <ListItemButton onClick={() => navigate(item.route)} selected={window.location.pathname === item.route}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
